import {
  areaUomAbbrev,
  concatValuesBy,
  defaultTxt,
  formatNumber,
  getPropertyTypeLabel,
  UserSettings,
} from '@miq/utils';
import { ReportRecord } from '../../../../../models/report.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { digger } from '@cbre/digger';
import {
  DOLLAR_SIGN,
  getCompanyAndContactInfoByRoles,
  GetCompanyAndContactInfoByRolesOptions,
  getUOMValue,
} from '../us.common';
import { map } from 'rxjs/operators';
import { AVAILABILITY_TYPES } from '../../common';

const AVAILABILITY_TYPE_FIELD = 'ref_availability_type_desc';

export const displayFnMap: TemplateFieldsDisplayFn = {
  300: {
    // 300 Property Address
    displayFn: (record: ReportRecord, _, street, city, state, zipCode) => {
      const cityState = concatValuesBy(
        ', ',
        mapAndFormatDynamicValue(record, city),
        mapAndFormatDynamicValue(record, state)
      );
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, street),
        concatValuesBy(' ', cityState, mapAndFormatDynamicValue(record, zipCode))
      );
      return values;
    },
  },
  301: {
    // 301 Property Type
    displayFn: (record: ReportRecord, _, _type, subType) => {
      const propertyType = mapAndFormatDynamicValue(record, _type);
      const typeValues = Array.isArray(propertyType) ? propertyType : propertyType ? [propertyType] : [];

      const values = concatValuesBy(
        ' \n',
        typeValues.map(getPropertyTypeLabel),
        mapAndFormatDynamicValue(record, subType)
      );
      return values;
    },
  },
  302: {
    // 302 Available SF
    displayFn: (record: ReportRecord, _, space) => {
      const values = mapAndFormatDynamicValue(record, space, [], false);
      return values;
    },
  },
  303: {
    // 303 Availability Type (L/S)
    displayFn: displayFnWrapper,
  },
  304: {
    // 304 Floor | Suite
    displayFn: (record: ReportRecord, _, suite, floor) => {
      const values = concatValuesBy(
        ' | ',
        mapAndFormatDynamicValue(record, floor),
        mapAndFormatDynamicValue(record, suite)
      );
      return values;
    },
  },
  305: {
    // 305 Lease Type
    displayFn: displayFnWrapper,
  },
  306: {
    // 306 Vacant (Y/N)
    displayFn: (record: ReportRecord, _, vacant) => {
      return mapAndFormatDynamicValue(record, vacant, null, null, true).toString().substr(0, 1);
    },
  },
  307: {
    // 307 Date On Market Possession
    displayFn: (record: ReportRecord, _, date, possession) => {
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, date),
        mapAndFormatDynamicValue(record, possession)
      ).toUpperCase();
      return values;
    },
  },
  308: {
    // 308 Asking Rate / Price Asking Rate Type
    displayFn: (record: ReportRecord, _, price, type, minRateMth, maxRateMth, minRateYr, maxRateYr) => {
      // When Availability type is Sale, only asking price should display. When Sale or Lease, put Sale price in its own row after Lease.
      const availType = digger(record.data, AVAILABILITY_TYPE_FIELD);
      const saleTypes = [
        AVAILABILITY_TYPES.sale.toUpperCase(),
        AVAILABILITY_TYPES.investmentSale.toUpperCase(),
        AVAILABILITY_TYPES.userSale.toUpperCase(),
      ];
      if (saleTypes.includes(availType.trim().toUpperCase())) {
        const [sales] = mapAndFormatDynamicValue(record, price, [], false);
        const salePrice = sales == null ? defaultTxt : sales;
        const saleValue = salePrice === defaultTxt ? defaultTxt : DOLLAR_SIGN + sales;
        return saleValue;
      }

      const isYearlyPref = UserSettings.isYearlyPreference();
      let leaseRates;
      if (isYearlyPref) {
        leaseRates = getAskingRates(record, minRateYr, maxRateYr, ' Yr');
      } else {
        leaseRates = getAskingRates(record, minRateMth, maxRateMth, ' Mth');
      }
      const leaseType = mapAndFormatDynamicValue(record, type);
      const leases = concatValuesBy(' \n', leaseRates, leaseType);

      const saleOrLease = [AVAILABILITY_TYPES.saleOrLease.toUpperCase(), AVAILABILITY_TYPES.unknown.toUpperCase()];
      if (saleOrLease.includes(availType.trim().toUpperCase())) {
        const [sales] = mapAndFormatDynamicValue(record, price, [], false);
        const salePrice = sales == null ? defaultTxt : sales;
        const saleValue = salePrice === defaultTxt ? defaultTxt : DOLLAR_SIGN + sales;

        const leaseAndSale = concatValuesBy(' \n', leases, saleValue);
        return leaseAndSale;
      }

      return leases;
    },
  },
  309: {
    // 309 Listing Rep Listing Rep Broker
    displayFn: (record: ReportRecord) => {
      const options: GetCompanyAndContactInfoByRolesOptions = { companySeparator: ';\n' };
      const getListingRepsContacts = getCompanyAndContactInfoByRoles('Listing Representative');
      return getListingRepsContacts(record.data, options);
    },
  },
  331: {
    // 329 - Total Available SF
    displayFn: (record: ReportRecord[], _, space) => {
      let totalSize = 0;
      let uom = null;
      record.forEach(rec => {
        const size = digger(rec.data, space.field);
        const uomValue = digger(rec.data, space.uomArgument.field);
        uom = getUOMValue(uom, uomValue);
        if (size) {
          totalSize += parseInt(size);
        }
      });
      return space.uomArgument.lovValues.pipe(
        map((currencyLOV: LovValues[]) => {
          if (uom && currencyLOV) {
            const lovValue = currencyLOV.find(a => a.key === uom);
            if (lovValue) {
              uom = areaUomAbbrev(lovValue.value);
            }
          }
          return formatNumber(totalSize) + ' ' + (uom || '');
        })
      );
    },
  },
};

function getAskingRates(record, min, max, suffix) {
  const [rMin] = mapAndFormatDynamicValue(record, min, [], false) || [null];
  const [rMax] = mapAndFormatDynamicValue(record, max, [], false) || [null];
  const rateMin = rMin == null ? defaultTxt : rMin;
  const rateMax = rMax == null ? defaultTxt : rMax;

  // Early return for cases where no valid data is available
  if ((rateMin === defaultTxt && rateMax === defaultTxt) || (!min.field && !max.field)) {
    return defaultTxt;
  }

  // Handle cases where min or max fields are not available in catalog
  if (!min.field) {
    return DOLLAR_SIGN + rateMax + suffix;
  }
  if (!max.field) {
    return DOLLAR_SIGN + rateMin + suffix;
  }

  // Both fields available - create range
  return concatValuesBy(' - ', DOLLAR_SIGN + rateMin, DOLLAR_SIGN + rateMax) + suffix;
}
